/**
 * SignatureCanvas — Touch/Maus-fähiges Unterschriftenfeld.
 *
 * Liefert die Unterschrift als PNG-Data-URL an `onSignatureChange`.
 * Passt sich responsiv an. Enthält Touch-Optimierungen (preventDefault Scroll).
 */
import { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, Check } from "@phosphor-icons/react";

export default function SignatureCanvas({ value, onSignatureChange, height = 140,
                                         placeholder = "Hier unterschreiben — mit Maus oder Finger", disabled = false }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(!!value);
  const lastPointRef = useRef(null);

  // Resize + initial load
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * dpr;
    cv.height = rect.height * dpr;
    const ctx = cv.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = "#0F172A";
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = value;
      setHasContent(true);
    } else {
      ctx.clearRect(0, 0, rect.width, rect.height);
    }
  }, [value]);

  const pos = (e) => {
    const cv = canvasRef.current;
    const rect = cv.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDrawing(true);
    lastPointRef.current = pos(e);
  };
  const draw = (e) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    setHasContent(true);
  };
  const stop = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    // export as PNG
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSignatureChange?.(dataUrl);
  };

  const clear = () => {
    const cv = canvasRef.current;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, cv.width, cv.height);
    setHasContent(false);
    onSignatureChange?.("");
  };

  return (
    <div className="space-y-1.5" data-testid="signature-canvas-wrap">
      <div
        className="relative bg-white border border-slate-300 rounded-md overflow-hidden"
        style={{ height }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={stop}
          className="w-full h-full touch-none cursor-crosshair"
          data-testid="signature-canvas"
        />
        {!hasContent && !disabled && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-[11px] text-slate-400 italic">
            {placeholder}
          </div>
        )}
      </div>
      {!disabled && (
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-500">
            {hasContent
              ? <><Check size={11} className="inline text-emerald-600" /> Signatur erfasst</>
              : "Unterschrift fehlt noch"}
          </span>
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1"
                  onClick={clear} data-testid="signature-canvas-clear">
            <Eraser size={12} /> Löschen
          </Button>
        </div>
      )}
    </div>
  );
}
